import type {
	BannerBranding,
	BannerColors,
	BannerConfig,
	BannerLayout,
	BannerStyle,
	BannerTextSet,
	WidgetConfig,
} from "./types";

/**
 * What the server (or an old cache) may actually send: any field absent, and
 * nested objects themselves partial — e.g. a pre-v2 payload carries 6-key
 * texts and 3-key colors.
 */
export type PartialBannerConfig = Omit<
	Partial<BannerConfig>,
	"texts" | "colors" | "style" | "branding"
> & {
	texts?: Partial<BannerTextSet>;
	colors?: Partial<BannerColors>;
	style?: Partial<BannerStyle>;
	branding?: Partial<BannerBranding>;
};

const CACHE_KEY = "cookieguard_config";
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface CachedConfig {
	data: WidgetConfig;
	timestamp: number;
}

/** Contract §5 default copy — original FewCookies wording. */
const DEFAULT_BANNER_TEXTS_V2: Record<"ro" | "en", BannerTextSet> = {
	ro: {
		title: "Folosim cookie-uri",
		description:
			"Folosim cookie-uri pentru ca site-ul să funcționeze corect, pentru a personaliza conținutul, pentru a oferi funcții de social media și pentru a analiza traficul. Poți alege mai jos ce categorii de cookie-uri accepți.",
		acceptAll: "Acceptă toate",
		rejectAll: "Respinge toate",
		save: "Acceptă selecția",
		customize: "Personalizează",
		tabConsent: "Consimțământ",
		tabDetails: "Detalii",
		tabAbout: "Despre",
		selectLabel: "Selectează din lista de mai jos:",
		readMore: "Citește mai multe",
		about:
			"Acest site utilizează mai multe tipuri de cookie-uri. Unele sunt plasate de site-ul nostru, altele de servicii terțe integrate în paginile noastre.\n\nPrin opțiunile din acest banner decizi ce informații colectăm despre navigarea ta. Îți poți schimba sau retrage oricând consimțământul, folosind butonul rotund din colțul paginii.\n\nÎn funcție de rolul lor, cookie-urile sunt împărțite în categorii: necesare, funcționale, analitice și de marketing. Detaliile fiecărei categorii sunt disponibile în fila „Detalii”.",
	},
	en: {
		title: "We use cookies",
		description:
			"We use cookies to keep this site working properly, personalize content, provide social media features and analyze our traffic. Choose below which cookie categories you accept.",
		acceptAll: "Accept all",
		rejectAll: "Reject all",
		save: "Accept selection",
		customize: "Customize",
		tabConsent: "Consent",
		tabDetails: "Details",
		tabAbout: "About",
		selectLabel: "Select from the list below:",
		readMore: "Read more",
		about:
			"This site uses several types of cookies. Some are set by our site, others by third-party services embedded in our pages.\n\nThe choices in this banner decide what information we collect about your visit. You can change or withdraw your consent at any time using the round button in the corner of the page.\n\nBased on their role, cookies fall into categories: necessary, functional, analytics and marketing. Details for each category are available in the “Details” tab.",
	},
};

/** Legacy `position` → v2 `layout` mapping. */
const LEGACY_POSITION_TO_LAYOUT: Record<string, BannerLayout> = {
	bottom: "bar-bottom",
	top: "bar-top",
	"center-modal": "modal",
};

export async function loadConfig(apiKey: string, apiBase: string): Promise<WidgetConfig> {
	// 1. Try sessionStorage cache
	const cached = readCache(apiKey);
	if (cached) return withMergedConfig(apiKey, cached);

	// 2. Fetch from API
	try {
		const response = await fetch(`${apiBase}/widget/config/${apiKey}`);
		if (response.ok) {
			// Defensive: a server config that omits banner fields (e.g. a site whose
			// stored config is just `{ language }`, or a pre-v2 payload) must still
			// render rather than crash the banner. Fill any gaps from the defaults.
			const data = withMergedConfig(apiKey, (await response.json()) as WidgetConfig);
			writeCache(apiKey, data);
			return data;
		}
	} catch {
		// Network error — try offline fallback
	}

	// 3. Try expired cache as offline fallback
	const expired = readCache(apiKey, true);
	if (expired) return withMergedConfig(apiKey, expired);

	// 4. Fallback to data-* attributes on script tag
	const inlineConfig = readDataAttributes();
	if (inlineConfig) return withMergedConfig(apiKey, inlineConfig);

	// 5. Return default config
	return getDefaultWidgetConfig(apiKey);
}

/**
 * Caches/inline payloads may predate v2 or omit envelope fields entirely (an
 * inline data-attribute payload is arbitrary site-author JSON) — complete the
 * banner config AND normalize the envelope so init() can rely on the shape.
 */
function withMergedConfig(apiKey: string, data: Partial<WidgetConfig>): WidgetConfig {
	return {
		version: typeof data.version === "number" ? data.version : 0,
		config: mergeBannerConfig(apiKey, data.config),
		cookies: Array.isArray(data.cookies) ? data.cookies : [],
		...(Array.isArray(data.detectedCookies) ? { detectedCookies: data.detectedCookies } : {}),
	};
}

function readCache(apiKey: string, ignoreExpiry = false): WidgetConfig | null {
	try {
		const raw = sessionStorage.getItem(`${CACHE_KEY}:${apiKey}`);
		if (!raw) return null;

		const cached = JSON.parse(raw) as CachedConfig;
		if (!ignoreExpiry && Date.now() - cached.timestamp > CACHE_TTL_MS) {
			return null;
		}
		return cached.data;
	} catch {
		return null;
	}
}

function writeCache(apiKey: string, data: WidgetConfig): void {
	try {
		const cached: CachedConfig = { data, timestamp: Date.now() };
		sessionStorage.setItem(`${CACHE_KEY}:${apiKey}`, JSON.stringify(cached));
	} catch {
		// sessionStorage full or unavailable — ignore
	}
}

function readDataAttributes(): WidgetConfig | null {
	const script = document.querySelector<HTMLScriptElement>("script[data-cookieguard-config]");
	if (!script) return null;

	try {
		const raw = script.getAttribute("data-cookieguard-config");
		if (!raw) return null;
		return JSON.parse(raw) as WidgetConfig;
	} catch {
		return null;
	}
}

/**
 * Complete a (possibly partial or legacy) banner config from the built-in
 * defaults. The server is expected to send a fully-resolved v2 config, but a
 * freshly-created site can return just `{ language }` and pre-v2 payloads only
 * carry `position` + 6-key texts. Every v2 field is guaranteed present after
 * this merge, including the legacy `position` → `layout` mapping.
 */
export function mergeBannerConfig(
	apiKey: string,
	partial: PartialBannerConfig | undefined,
): BannerConfig {
	const defaults = getDefaultWidgetConfig(apiKey).config;
	const p = partial ?? {};

	const language: "ro" | "en" = p.language === "en" ? "en" : "ro";
	const layout: BannerLayout =
		p.layout ??
		(p.position !== undefined ? LEGACY_POSITION_TO_LAYOUT[p.position] : undefined) ??
		defaults.layout;

	// Base colors first, then derive the extended ones from the *resolved* base
	// so a legacy payload with a custom primary gets a matching accent.
	const base = { ...defaults.colors, ...(p.colors ?? {}) };
	const colors = {
		primary: base.primary,
		background: base.background,
		text: base.text,
		accent: p.colors?.accent ?? base.primary,
		buttonText: p.colors?.buttonText ?? "#ffffff",
		surface: p.colors?.surface ?? base.background,
		border: p.colors?.border ?? "",
	};

	return {
		layout,
		theme: p.theme ?? defaults.theme,
		colors,
		texts: { ...DEFAULT_BANNER_TEXTS_V2[language], ...(p.texts ?? {}) },
		privacyPolicyUrl: p.privacyPolicyUrl ?? defaults.privacyPolicyUrl,
		language,
		categories: p.categories ?? defaults.categories,
		footer: p.footer ?? defaults.footer,
		style: {
			radius: p.style?.radius ?? 14,
			shadow: p.style?.shadow ?? "soft",
			font: p.style?.font ?? "system",
		},
		branding: {
			logo: p.branding?.logo ?? null,
			logoAlt: p.branding?.logoAlt ?? null,
			poweredBy: p.branding?.poweredBy ?? true,
			reopen: p.branding?.reopen ?? true,
			reopenPosition: p.branding?.reopenPosition ?? "left",
		},
	};
}

export function getDefaultWidgetConfig(apiKey: string): WidgetConfig {
	const config: BannerConfig = {
		layout: "bar-bottom",
		theme: "light",
		colors: {
			primary: "#2563eb",
			background: "#ffffff",
			text: "#1f2937",
			accent: "#2563eb",
			buttonText: "#ffffff",
			surface: "#ffffff",
			border: "",
		},
		texts: { ...DEFAULT_BANNER_TEXTS_V2.ro },
		privacyPolicyUrl: null,
		language: "ro",
		categories: [
			{
				key: "necessary",
				label: "Necesare",
				description: "Cookie-urile necesare sunt esențiale pentru funcționarea site-ului.",
				required: true,
			},
			{
				key: "functional",
				label: "Funcționale",
				description:
					"Cookie-urile funcționale permit funcționalități și personalizare îmbunătățite.",
				required: false,
			},
			{
				key: "analytics",
				label: "Analitice",
				description:
					"Cookie-urile analitice ne ajută să înțelegem cum interacționează vizitatorii cu site-ul.",
				required: false,
			},
			{
				key: "marketing",
				label: "Marketing",
				description: "Cookie-urile de marketing sunt folosite pentru a afișa reclame relevante.",
				required: false,
			},
		],
		footer: {},
		style: { radius: 14, shadow: "soft", font: "system" },
		branding: { logo: null, logoAlt: null, poweredBy: true, reopen: true, reopenPosition: "left" },
	};

	void apiKey;
	return { version: 0, config, cookies: [] };
}
